import React, { useState } from 'react';
import { SEO } from '../components/SEO';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faEye, faEyeSlash, faCircleExclamation, faRightToBracket, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const { accessToken } = await response.json();
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('token', accessToken);
        window.location.href = '/dashboard';
      } else {
        const err = await response.json();
        setError(err.message || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Login | GHLWA - WhatsApp Integration for GoHighLevel"
        description="Login to your GHLWA account and manage WhatsApp integrations for your GoHighLevel locations."
        canonical="https://app.ghlwa.com/login"
      />
      <div className="min-h-screen flex">
        {/* ── Left branding panel ── */}
        <div className="auth-left-panel">
          {/* decorative circles */}
          <div className="absolute top-[-80px] right-[-80px] w-72 h-72 rounded-full opacity-10" style={{ background: '#6366f1' }} />
          <div className="absolute bottom-[-60px] left-[-60px] w-56 h-56 rounded-full opacity-10" style={{ background: '#f59e0b' }} />

          <div className="relative z-10 flex flex-col items-center text-center">
            <img src="/ghlwa.png" alt="GHLWA Logo" className="h-24 w-auto mb-8 drop-shadow-xl" />
            <h1 className="text-4xl font-extrabold text-white mb-3 tracking-tight">GHLWA</h1>
            <p className="text-indigo-200 text-lg mb-10 max-w-xs leading-relaxed">
              Seamless WhatsApp messaging for your GHL locations
            </p>

            {/* Feature bullets */}
            <div className="space-y-4 w-full max-w-xs text-left">
              {[
                { icon: faWhatsapp, text: 'Route messages across all your locations' },
                { icon: faWhatsapp, text: 'Flexible per-user or shared number setup' },
                { icon: faWhatsapp, text: 'Auto-sync contacts with GoHighLevel' },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={f.icon} className="text-indigo-300 text-sm" />
                  </span>
                  <span className="text-indigo-100 text-sm">{f.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Right form panel ── */}
        <div className="auth-right-panel">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="flex justify-center mb-8 lg:hidden">
              <img src="/ghlwa.png" alt="GHLWA" className="h-16 w-auto" />
            </div>

            <h2 className="text-3xl font-extrabold text-secondary-900 mb-2">Sign in to GHLWA</h2>
            <p className="text-secondary-500 mb-8">Access your messaging workspace</p>

            {error && (
              <div className="mb-5 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                <FontAwesomeIcon icon={faCircleExclamation} />
                {error}
              </div>
            )}

            <form className="space-y-5" onSubmit={handleLogin}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-1.5">
                  Email address
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-secondary-400 pointer-events-none">
                    <FontAwesomeIcon icon={faEnvelope} className="text-sm" />
                  </span>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field pl-10"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="password" className="block text-sm font-medium text-secondary-700">
                    Password
                  </label>
                  <a href="/forgot-password" className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors">
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-secondary-400 pointer-events-none">
                    <FontAwesomeIcon icon={faLock} className="text-sm" />
                  </span>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pl-10 pr-11"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-secondary-400 hover:text-secondary-600 transition-colors"
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="text-sm" />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              >
                {loading ? (
                  <><FontAwesomeIcon icon={faSpinner} className="animate-spin" /> Signing in...</>
                ) : (
                  <><FontAwesomeIcon icon={faRightToBracket} /> Sign in</>
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-secondary-600">
              Don't have an account?{' '}
              <a href="/register" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">
                Sign up
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};


